"use client";

import React from "react";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Vehicle } from "@/types";
import { Search, X } from "lucide-react";

interface VehicleMultiSelectProps {
  vehicles: Vehicle[];
  selectedVehicleIds: string[];
  onSelectionChange: (vehicleIds: string[]) => void;
  disabled?: boolean;
  error?: string;
  searchTerm: string;
  onSearchChange: (term: string) => void;
}

export function VehicleMultiSelect({
  vehicles,
  selectedVehicleIds,
  onSelectionChange,
  disabled = false,
  error,
  searchTerm,
  onSearchChange,
}: VehicleMultiSelectProps) {
  const handleToggle = (vehicleId: string) => {
    if (disabled) return;

    const isSelected = selectedVehicleIds.includes(vehicleId);

    if (isSelected) {
      onSelectionChange(selectedVehicleIds.filter((id) => id !== vehicleId));
    } else {
      onSelectionChange([...selectedVehicleIds, vehicleId]);
    }
  };

  const handleRemove = (vehicleId: string) => {
    if (disabled) return;
    onSelectionChange(selectedVehicleIds.filter((id) => id !== vehicleId));
  };

  // Get selected vehicle details for chips
  const selectedVehicles = vehicles.filter((v) =>
    selectedVehicleIds.includes(v.id)
  );

  return (
    <div className="space-y-3">
      {/* Selected Vehicles Chips */}
      {selectedVehicles.length > 0 && (
        <div className="space-y-2">
          <Label className="text-xs text-muted-foreground">
            Selected Vehicles:
          </Label>
          <div className="flex flex-wrap gap-2">
            {selectedVehicles.map((vehicle) => (
              <Badge
                key={vehicle.id}
                variant="secondary"
                className="px-3 py-1.5 text-sm font-medium flex items-center gap-2 bg-slate-100 border border-slate-300 text-slate-900"
              >
                <span>{vehicle.plateNumber}</span>
                {!disabled && (
                  <button
                    type="button"
                    onClick={() => handleRemove(vehicle.id)}
                    className="hover:bg-primary/20 rounded-full p-0.5 transition-colors"
                  >
                    <X className="h-3 w-3" />
                  </button>
                )}
              </Badge>
            ))}
          </div>
        </div>
      )}

      {/* Search Input */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <Input
          placeholder="Search by plate, make or model..."
          value={searchTerm}
          onChange={(e) => onSearchChange(e.target.value)}
          className="pl-9"
          disabled={disabled}
        />
      </div>

      {/* Vehicle List with Checkboxes */}
      <div
        className={`space-y-1 max-h-48 overflow-y-auto border rounded-lg p-2 ${
          disabled ? "opacity-50 pointer-events-none" : ""
        }`}
      >
        {vehicles.length === 0 ? (
          <p className="text-sm text-muted-foreground text-center py-4">
            No vehicles found for this customer
          </p>
        ) : (
          vehicles.map((vehicle) => {
            const isSelected = selectedVehicleIds.includes(vehicle.id);
            return (
              <div
                key={vehicle.id}
                className={`flex items-center space-x-3 p-2.5 rounded-md cursor-pointer transition-colors ${
                  isSelected
                    ? "bg-primary/10 border border-primary/30"
                    : "hover:bg-muted border border-transparent"
                }`}
                onClick={() => handleToggle(vehicle.id)}
              >
                <Checkbox
                  id={`vehicle-${vehicle.id}`}
                  checked={isSelected}
                  onCheckedChange={() => handleToggle(vehicle.id)}
                  disabled={disabled}
                />
                <Label
                  htmlFor={`vehicle-${vehicle.id}`}
                  className="flex-1 cursor-pointer"
                >
                  <div className="font-medium text-sm">
                    {String(vehicle.make || "")} {String(vehicle.model || "")}
                  </div>
                  <div className="text-xs text-muted-foreground">
                    {vehicle.plateNumber}
                    {vehicle.color && ` • ${vehicle.color}`}
                  </div>
                </Label>
              </div>
            );
          })
        )}
      </div>

      {/* Error Message */}
      {error && <p className="text-sm text-destructive">{error}</p>}
    </div>
  );
}
